/**
 * Window placement — one-shot and cascade session.
 *
 * One-shot: `setNextWindowPlacement(pos)` — the next `createWindow` call lands
 * at that world point, then the session clears. Used by GlobalEntitySearch,
 * quick-create chips, and context-menu create actions.
 *
 * Cascade session: `beginWindowPlacementSession(anchor, opts)` — successive
 * `createWindow` calls fan out from the anchor with a small step offset so
 * multiple agent-produced windows cluster visually around the click point.
 * Used by the SlimCreatePalette AI submit path.
 *
 * Both paths share `consumeNextWindowPlacement()` as the single reader,
 * called by `createWindow` in spaceActions.ts.
 *
 * Debug: localStorage.debug_slim_palette = '1' or ?debug_slim_palette=1
 */

function isDebug(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return (
      localStorage.getItem('debug_slim_palette') === '1' ||
      new URLSearchParams(window.location.search).get('debug_slim_palette') === '1'
    );
  } catch {
    return false;
  }
}

interface PlacementSession {
  anchor: { x: number; y: number };
  count: number;
  maxWindows: number;
  offsetStep: number;
  expiresAt: number;
}

let session: PlacementSession | null = null;

/**
 * Request that the next created window be positioned at this world point.
 * Back-compatible with the previous single-placement API: always creates a
 * session with maxWindows=1 and offsetStep=0.
 */
export function setNextWindowPlacement(position: { x: number; y: number }): void {
  session = {
    anchor: position,
    count: 0,
    maxWindows: 1,
    offsetStep: 0,
    expiresAt: Date.now() + 20_000,
  };
  if (isDebug()) {
    console.log('[WINDOW_PLACEMENT] one-shot set', position);
    updateDebugGlobal();
  }
}

/** Clear any pending session (e.g. when a request is abandoned). */
export function clearNextWindowPlacement(): void {
  session = null;
}

/**
 * Begin a cascade session for multiple windows produced by a single AI run.
 * Each call to `consumeNextWindowPlacement` returns a position offset from
 * the anchor by `count * offsetStep` in both X and Y, producing a diagonal
 * fan. The session expires after `ttlMs` ms or once `maxWindows` are consumed.
 */
export function beginWindowPlacementSession(
  anchor: { x: number; y: number },
  opts: { maxWindows?: number; ttlMs?: number; offsetStep?: number } = {},
): void {
  const { maxWindows = 8, ttlMs = 45_000, offsetStep = 28 } = opts;
  session = {
    anchor,
    count: 0,
    maxWindows,
    offsetStep,
    expiresAt: Date.now() + ttlMs,
  };
  if (isDebug()) {
    console.log('[WINDOW_PLACEMENT] session begin', { anchor, maxWindows, ttlMs, offsetStep });
    updateDebugGlobal();
  }
}

/**
 * Consume the next placement from the active session. Returns a world-space
 * position or `null` when no session is active / it has expired. Advances
 * the internal counter and clears the session when exhausted.
 */
export function consumeNextWindowPlacement(): { x: number; y: number } | null {
  if (!session) return null;
  if (Date.now() > session.expiresAt) {
    if (isDebug()) console.log('[WINDOW_PLACEMENT] session expired, clearing');
    session = null;
    return null;
  }

  const { anchor, count, maxWindows, offsetStep } = session;
  const pos = {
    x: anchor.x + count * offsetStep,
    y: anchor.y + count * offsetStep,
  };

  session.count += 1;
  if (session.count >= maxWindows) {
    if (isDebug()) console.log('[WINDOW_PLACEMENT] session exhausted after', session.count, 'windows');
    session = null;
  }

  if (isDebug()) {
    console.log('[WINDOW_PLACEMENT] consume', { pos, count, remaining: session ? session.maxWindows - session.count : 0 });
    updateDebugGlobal();
  }
  return pos;
}

function updateDebugGlobal(): void {
  try {
    (window as any).__slimPalette = {
      ...(window as any).__slimPalette,
      placement: session ? { anchor: session.anchor, count: session.count, maxWindows: session.maxWindows } : null,
    };
  } catch { /* non-fatal */ }
}
